import React from 'react';
import { CheckCircle2, AlertCircle, Database } from 'lucide-react';

interface GroundedStatusBadgeProps {
  isGrounded: boolean;
  groundedDate?: string | null;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
}

const GroundedStatusBadge: React.FC<GroundedStatusBadgeProps> = ({
  isGrounded,
  groundedDate,
  size = 'md',
  showLabel = true
}) => {
  const sizeClasses = {
    sm: 'text-xs px-1.5 py-0.5',
    md: 'text-sm px-2 py-1',
    lg: 'text-base px-3 py-1.5'
  };

  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (isGrounded) {
    return (
      <div
        className={`inline-flex items-center gap-1 rounded-full font-medium bg-green-100 text-green-700 ${sizeClasses[size]}`}
        title={groundedDate ? `Grounded on ${formatDate(groundedDate)}` : 'Based on real client data'}
      >
        <Database className={iconSizes[size]} />
        {showLabel && <span>Grounded</span>}
      </div>
    );
  }

  return (
    <div
      className={`inline-flex items-center gap-1 rounded-full font-medium bg-yellow-100 text-yellow-700 ${sizeClasses[size]}`}
      title="Based on estimated data - import metrics to ground with real data"
    >
      <AlertCircle className={iconSizes[size]} />
      {showLabel && <span>Estimated</span>}
    </div>
  );
};

interface GroundedProgressProps {
  grounded: number;
  total: number;
  className?: string;
}

export const GroundedProgress: React.FC<GroundedProgressProps> = ({
  grounded,
  total,
  className = ''
}) => {
  const percentage = total > 0 ? (grounded / total) * 100 : 0;

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <div className="flex-1">
        <div className="flex items-center justify-between text-sm mb-1">
          <span className="text-gray-600">Data Quality</span>
          <span className="font-medium text-gray-900">
            {grounded} / {total} grounded
          </span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${
              percentage === 100 ? 'bg-green-500' :
              percentage >= 50 ? 'bg-yellow-500' :
              'bg-red-400'
            }`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
      <span className={`text-sm font-semibold ${
        percentage === 100 ? 'text-green-600' :
        percentage >= 50 ? 'text-yellow-600' :
        'text-red-500'
      }`}>
        {percentage.toFixed(0)}%
      </span>
    </div>
  );
};

export default GroundedStatusBadge;
